"use client";

import { ApolloLink, HttpLink } from "@apollo/client";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from "@apollo/client-integration-nextjs";
import { buildAxiosFetch } from "@lifeomic/axios-fetch";
import axios from "axios";
import { createClient } from "graphql-ws";

import { generateGraphQLJWT } from "@/graphql/jwt-generator";
import {
  ENABLE_GRAPHQL_BATCH_LINK,
  GRAPH_TYPE_POLICIES,
} from "@/utils/constants";

let apolloClient: ApolloClient | null = null;

const TOKEN_REFRESH_INTERVAL = 1000 * 60;

let token: string | null = null;
let expiry = 0;
let tokenRequestPromise: Promise<string> | null = null;

if (process.env.NODE_ENV !== "production") {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

function initClient() {
  const isServer = typeof window === "undefined";

  const authLink = setContext(async (_, { headers }) => {
    if (!token || Date.now() > expiry) {
      const request = tokenRequestPromise ?? generateGraphQLJWT();
      tokenRequestPromise = request;
      token = await request.catch(() => null);
      expiry = Date.now() + TOKEN_REFRESH_INTERVAL;
      tokenRequestPromise = null;
    }

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const batchLink = new BatchHttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URI,
    batchMax: 10,
    batchInterval: 10,
    fetch: buildAxiosFetch(axios) as (
      input: RequestInfo | URL,
      init?: RequestInit | undefined,
    ) => Promise<Response>,
  });

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URI,
    fetch: buildAxiosFetch(axios) as (
      input: RequestInfo | URL,
      init?: RequestInit | undefined,
    ) => Promise<Response>,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      connectionParams: {},
      url:
        process.env.NEXT_PUBLIC_WS_GRAPHQL_URL ||
        "wss://default-graphql-ws-url.com/graphql",
    }),
  );

  const apolloLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    ENABLE_GRAPHQL_BATCH_LINK
      ? ApolloLink.split(
          (operation) => operation.getContext().bypassBatch,
          httpLink,
          batchLink,
        )
      : httpLink,
  );

  const retryLink = new RetryLink();

  // Log any GraphQL errors or network error that occurred
  const errorLink = onError(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const { message, locations, path } of error.errors) {
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      }
      return;
    }

    console.log(`[Network error]: ${error}`);
  });

  const links: ApolloLink[] = [errorLink, authLink, retryLink, apolloLink];

  if (isServer) {
    links.unshift(
      new SSRMultipartLink({
        stripDefer: true,
      }),
    );
  }

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: GRAPH_TYPE_POLICIES,
    }),
    link: ApolloLink.from(links),
  });
}

export function makeClient() {
  const _apolloClient = apolloClient ?? initClient();
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function ApolloProvider({
  children,
}: Readonly<React.PropsWithChildren>) {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
}
